import {
  and,
  eq,
  useLiveQuery as useTanstackLiveQuery,
} from "@tanstack/react-db";
import {
  pickCallerGuest,
  pickCallerStay,
  stripTransportPrefix,
} from "@vidimo/shared";
import { useMemo } from "react";
import { guestIdentitiesCollection } from "@/lib/collections/guest-identities";
import type { Guest } from "@/lib/collections/guests";
import { guestsCollection } from "@/lib/collections/guests";
import { reservationUnitGuestsCollection } from "@/lib/collections/reservation-unit-guests";
import { reservationUnitsCollection } from "@/lib/collections/reservation-units";
import type { Reservation } from "@/lib/collections/reservations";
import { reservationsCollection } from "@/lib/collections/reservations";
import { unitsCollection } from "@/lib/collections/units";

/**
 * Resolves a Twilio `from` field (E.164 or `whatsapp:+...`) to the local
 * guest record + their most relevant reservation + unit, so the call UI
 * can show "Thomas Dubois · Room 101 · Checked In" instead of a raw
 * phone number.
 *
 * Returns `null` while loading; otherwise a fully-resolved struct (with
 * `guest === null` when no match was found).
 *
 * Only guests of `propertyId` match, since a number can belong to guests of
 * several properties. A null property searches them all: an active call
 * carries no property, and an invite whose `to` names none still resolves.
 *
 * Guests are matched by `guests.phone` and by `guest_identities.externalId`
 * (a WhatsApp identity whose number differs from the guest's primary
 * `phone`), and the stay is chosen across the ones they are registered on and
 * booked, with the shared `pickCallerGuest` and `pickCallerStay`: the API's
 * lock-screen label makes the same picks.
 */

const NO_MATCH = "__no_match__";

interface CallerLookup {
  guest: Guest | null;
  /** Most relevant reservation for the caller, or null if none / cancelled. */
  reservation: Reservation | null;
  /** Unit code (e.g. "101"). */
  unitCode: string | null;
}

/** Strip `whatsapp:` / `tel:` / `sip:` prefixes and trim. Returns null on empty. */
function normalize(rawFrom: string | null | undefined): string | null {
  if (!rawFrom) {
    return null;
  }
  const stripped = stripTransportPrefix(rawFrom).trim();
  return stripped || null;
}

export function useCallerLookup(
  rawFrom: string | null | undefined,
  propertyId: string | null
): CallerLookup {
  const normalized = normalize(rawFrom) ?? NO_MATCH;

  // Two parallel live queries — TanStack live queries cache by their key
  // function args so re-renders don't re-run them needlessly.

  const { data: guestsByPhone = [] } = useTanstackLiveQuery(
    (q) =>
      q
        .from({ g: guestsCollection })
        .where(({ g }) =>
          propertyId
            ? and(eq(g.phone, normalized), eq(g.propertyId, propertyId))
            : eq(g.phone, normalized)
        )
        .select(({ g }) => g),
    [normalized, propertyId]
  );

  const { data: guestsByIdentity = [] } = useTanstackLiveQuery(
    (q) =>
      q
        .from({ gi: guestIdentitiesCollection })
        .leftJoin({ g: guestsCollection }, ({ gi, g }) => eq(gi.guestId, g.id))
        .where(({ gi }) =>
          propertyId
            ? and(eq(gi.externalId, normalized), eq(gi.propertyId, propertyId))
            : eq(gi.externalId, normalized)
        )
        .select(({ g }) => g),
    [normalized, propertyId]
  );

  // The same picks the lock screen's label makes server-side.
  const guest = useMemo<Guest | null>(
    () =>
      pickCallerGuest(
        guestsByPhone as Guest[],
        (guestsByIdentity as (Guest | undefined)[]).filter(
          (g): g is Guest => g !== undefined
        )
      ),
    [guestsByPhone, guestsByIdentity]
  );

  // The stays the guest is registered on, and the ones they booked: a booker
  // calls before anyone has registered. Empty guest -> filter never matches.
  const guestId = guest?.id ?? NO_MATCH;
  const { data: registeredRows = [] } = useTanstackLiveQuery(
    (q) =>
      q
        .from({ rg: reservationUnitGuestsCollection })
        .innerJoin({ ru: reservationUnitsCollection }, ({ rg, ru }) =>
          eq(rg.reservationUnitId, ru.id)
        )
        .innerJoin({ r: reservationsCollection }, ({ ru, r }) =>
          eq(ru.reservationId, r.id)
        )
        .leftJoin({ u: unitsCollection }, ({ ru, u }) => eq(ru.unitId, u.id))
        .where(({ rg }) => eq(rg.guestId, guestId))
        .select(({ r, u }) => ({ reservation: r, unitCode: u.code })),
    [guestId]
  );
  const { data: bookedRows = [] } = useTanstackLiveQuery(
    (q) =>
      q
        .from({ r: reservationsCollection })
        .innerJoin({ ru: reservationUnitsCollection }, ({ r, ru }) =>
          eq(ru.reservationId, r.id)
        )
        .leftJoin({ u: unitsCollection }, ({ ru, u }) => eq(ru.unitId, u.id))
        .where(({ r }) => eq(r.bookerGuestId, guestId))
        .select(({ r, u }) => ({ reservation: r, unitCode: u.code })),
    [guestId]
  );

  const top = useMemo(() => {
    const asStays = (rows: typeof registeredRows) =>
      rows.map((row) => ({
        ...row,
        checkInAt: row.reservation.checkInAt,
        id: row.reservation.id,
        status: row.reservation.status,
      }));
    return pickCallerStay(asStays(registeredRows), asStays(bookedRows));
  }, [registeredRows, bookedRows]);

  return {
    guest,
    reservation: top?.reservation ?? null,
    unitCode: top?.unitCode ?? null,
  };
}
